import { createAsyncThunk } from '@reduxjs/toolkit'
import center from '@turf/center'
import { MultiPolygon, Polygon } from 'geojson'
import Point from 'ol/geom/Point'
import { Style } from 'ol/style'
import aerialPhotographyServiceAPI from '../../apis/AerialPhotographyServiceAPI'
import { forceManualRecaptcha, RecaptchaAction, RecaptchaPayload } from '../../common/slice'
import { RootState } from '../../common/store'
import { PayloadAction } from '../../common/types'
import RecaptchaRequiredError from '../../errors/RecaptchaRequiredError'
import { LocationData } from '../map/MapComponent'
import { APFrameDetailsType } from './ap.types'
import { APUtils } from './ap.util'
import {
  AP_FRAMES,
  setActiveYear,
  setAllFrames,
  setVisibleFrames,
  setYears
} from './apFrames.slice'

export const SELECT_AP_PARCEL = 'SELECT_AP_PARCEL'
export const SELECT_AP_PARCEL_BY_POLYGON = 'SELECT_AP_PARCEL_BY_POLYGON'
export const SELECT_AP_PARCEL_BY_FILM = 'SELECT_AP_PARCEL_BY_FILM'
export const UPDATE_AP_DATA = 'UPDATE_AP_DATA'
export const RESET_AP_DATA = 'RESET_AP_DATA'
export const UPDATE_AP_SEARCH_VALUE = 'UPDATE_AP_SEARCH_VALUE'
export const RESET_AP_SEARCH_VALUE = 'RESET_AP_SEARCH_VALUE'
export const UPDATE_AP_FRAME_DATA = 'UPDATE_AP_FRAME_DATA'
export const RESET_AP_FRAME_DATA = 'RESET_AP_FRAME_DATA'

export const FETCH_AP_PROJECT_DETAILS = 'FETCH_AP_PROJECT_DETAILS'
export const UPDATE_AP_PROJECT_DETAILS = 'UPDATE_AP_PROJECT_DETAILS'
export const RESET_AP_PROJECT_DETAILS = 'RESET_AP_PROJECT_DETAILS'

export const SEARCH_BY_POINT = 'searchByPoint'

export type APDataRoot = {
  error?: boolean
  errorType?: string
  errorMessage?: string
  lastUpdateTime?: number
  parcelData?: APDataType[]
}

export interface APDataType extends LocationData {
  parcelId: number
  laisKey: string
  address: string
  lotDetails: string
  landArea: string
  geometry: Polygon | MultiPolygon | undefined
}

export type APStyledLayerType = {
  fillColor: string,
  type: string | undefined,
  style: Style,
  className: string,
  data: APFrameDetailsType[]
}

// These next two are used to hold the data returned from the Aerial Photography Frame Data service
export type APFrameDataRoot = {
  error?: boolean
  errorType?: string
  errorMessage?: string
  lastUpdateTime?: number
  frameData: APFrameDataType[]
}

export interface APFrameDataType {
  frameId: number
  runId: number
  laiskey: string
  geometry?: Polygon | MultiPolygon
  centreGeometry?: Point | Polygon | MultiPolygon
}

export type APProjectDetailsRoot = {
  error?: boolean
  errorType?: string
  errorMessage?: string
  lastUpdateTime?: number
  laiskey: string
  frameDetails: APFrameDetailsType[]
  selectedFrameDetails?: APFrameDetailsType[]
  hoverFrameDetails?: APFrameDetailsType[]
  drawnPolygon?: boolean
}

export interface SearchFilters {
  address?: string,
  latitude?: number | string,
  longitude?: number | string,
  radius?: number
}

// Selectors
export const getAPData = (state: RootState) => {
  return state.apData
}

export const getAPFrameData = (state: RootState) => {
  return state.apFrameData
}

export const getAPProjectDetails = (state: RootState) => {
  return state.apProjectDetails
}

export const getSelectedActiveYear = (state: RootState) => {
  return state[AP_FRAMES].activeYear
}

export const updateAPData = (apData: APDataRoot) => {
  return {
    type: UPDATE_AP_DATA,
    payload: apData,
  }
}

export const updateAPProjectDetails = (apProjectDetailsData: APProjectDetailsRoot | null) => {
  return {
    type: UPDATE_AP_PROJECT_DETAILS,
    payload: apProjectDetailsData,
  }
}

export interface SelectedParcelProps extends RecaptchaPayload {
  LAISKEY?: string
  geometry?: Polygon | MultiPolygon
  filmNo?: string
  frameNo?: string
  projection?: string
  drawn?: boolean
}

export const updateAerialPhotographyReducer = (apData: APDataRoot | null = null, action: PayloadAction<APDataRoot>) => {

  if (action.type === UPDATE_AP_DATA) {
    return action.payload
  }

  if (action.type === RESET_AP_DATA) {
    return null
  }

  return apData
}

export const updateAPFrameDataReducer = (apFrameData: APFrameDataRoot | null = null, action: PayloadAction<APFrameDataRoot>) => {

  if (action.type === UPDATE_AP_FRAME_DATA) {
    return action.payload
  }

  if (action.type === RESET_AP_FRAME_DATA) {
    return null
  }

  return apFrameData
}

export const updateAPProjectDetailsReducer = (apProjectDetails: APProjectDetailsRoot | null = null, action: PayloadAction<APProjectDetailsRoot>) => {

  if (action.type === UPDATE_AP_PROJECT_DETAILS) {
    return action.payload
  }

  if (action.type === RESET_AP_PROJECT_DETAILS) {
    return null
  }

  return apProjectDetails
}


export const getSearchValue = (state: RootState) => {
  return state.apSearchValue
}

export const updateSearchValue = (searchValue?: string) => {
  return {
    type: UPDATE_AP_SEARCH_VALUE,
    payload: searchValue,
  }
}

export const selectParcel = createAsyncThunk(
  SELECT_AP_PARCEL,
  async (selectedParcel: SelectedParcelProps, thunkAPI) => {
    try {
      const payload = await aerialPhotographyServiceAPI.fetchAPData(selectedParcel)
      if (payload) {
        if (payload.error) {
          // TODO (JM) we should display something to the user here so they know
          // that the ILIS elves aren't going to be able to give them what they asked for
          console.log('Error reading selected parcel details!')
        } else {
          thunkAPI.dispatch(updateAPData({ ...payload, lastUpdateTime: new Date().getTime() }))
          const { apData } = thunkAPI.getState() as RootState
          const parcel = {
            geometry: apData?.parcelData ? apData.parcelData[0].geometry : undefined,
          }
          thunkAPI.dispatch(fetchProjectDetails(parcel))
        }
      }
    } catch (e) {
      if (e instanceof RecaptchaRequiredError) {
        const action: RecaptchaAction<SelectedParcelProps> = {
          type: SELECT_AP_PARCEL,
          payload: selectedParcel,
        }
        thunkAPI.dispatch(forceManualRecaptcha(action))
      } else {
        throw e
      }
    }
  })

export const selectParcelByPolygon = createAsyncThunk(
  SELECT_AP_PARCEL_BY_POLYGON,
  async (selectedParcel: SelectedParcelProps, thunkAPI) => {
    try {
      if (selectedParcel.geometry) {
        // Create a fake parcel with the polygon from the user
        const polyCenter = center(selectedParcel.geometry)

        const parcelData: APDataType = {
          address: '',
          geometry: selectedParcel.geometry,
          laisKey: '',
          landArea: '',
          latitude: polyCenter.geometry.coordinates[1],
          longitude: polyCenter.geometry.coordinates[0],
          lotDetails: '',
          parcelId: -10000000 - Math.floor(Math.random() * 99999999),
        }
        selectedParcel.drawn = true
        thunkAPI.dispatch(updateAPData({ parcelData: [parcelData], lastUpdateTime: new Date().getTime() }))
        thunkAPI.dispatch(fetchProjectDetails(selectedParcel))
      }
    } catch (e) {
      if (e instanceof RecaptchaRequiredError) {
        const action: RecaptchaAction<SelectedParcelProps> = {
          type: SELECT_AP_PARCEL_BY_POLYGON,
          payload: selectedParcel,
        }
        thunkAPI.dispatch(forceManualRecaptcha(action))
      } else {
        throw e
      }
    }
  })


export const selectParcelByFilmAndFrame = createAsyncThunk(
    SELECT_AP_PARCEL_BY_FILM,
    async (selectedParcel: SelectedParcelProps, thunkAPI) => {
      try {
        if (selectedParcel.frameNo && selectedParcel.filmNo) {
          const parcelData: APDataType = {
            address: '',
            geometry: selectedParcel.geometry,
            laisKey: '',
            landArea: '',
            latitude: '',
            longitude: '',
            lotDetails: '',
            parcelId: -10000000 - Math.floor(Math.random() * 99999999),
          }
          selectedParcel.drawn = false
          thunkAPI.dispatch(updateAPData({ parcelData: [parcelData], lastUpdateTime: new Date().getTime() }))
          thunkAPI.dispatch(fetchProjectDetails(selectedParcel))
        }
      } catch (e) {
        if (e instanceof RecaptchaRequiredError) {
          const action: RecaptchaAction<SelectedParcelProps> = {
            type: SELECT_AP_PARCEL_BY_FILM,
            payload: selectedParcel,
          }
          thunkAPI.dispatch(forceManualRecaptcha(action))
        } else {
          throw e
        }
      }
    })


export const resetAPSearchValue = () => {
  return {
    type: RESET_AP_SEARCH_VALUE,
  }
}

export const apSearchValueReducer = (searchValue = '', { type, payload }: {
  type: string,
  payload: string
}) => {

  if (type === UPDATE_AP_SEARCH_VALUE) {
    return payload
  }

  if (type === RESET_AP_SEARCH_VALUE) {
    return null
  }

  return searchValue
}

export const fetchProjectDetails = createAsyncThunk(
  FETCH_AP_PROJECT_DETAILS,
  async (selectedParcel: SelectedParcelProps, thunkAPI) => {
    try {
      const payload = await aerialPhotographyServiceAPI.fetchProjectDetails(selectedParcel)
      if (payload) {
        // TODO-Luke clean up this type so we don't have to do this BS
        thunkAPI.dispatch(updateAPProjectDetails({
          laiskey: '',
          frameDetails: [],
          ...payload,
          lastUpdateTime: new Date().getTime(),
        }))
        if ('frameDetails' in payload) {
          thunkAPI.dispatch(setAllFrames(payload.frameDetails))
          const yearsArrayFromFrames = APUtils.getYearsArrayFromFrames(payload.frameDetails)
          thunkAPI.dispatch(setYears(yearsArrayFromFrames))
          thunkAPI.dispatch(setVisibleFrames(APUtils.getFramesForYear(payload.frameDetails, yearsArrayFromFrames[0])))

          // If there is no year parameter specified then we'll default to the most recent year based
          // on what records we've read from the server
          const activeYear = getSelectedActiveYear(thunkAPI.getState() as RootState)

          // If the polygon has been drawn by the client, then automatically set the active year to "All" (0)
          // and set the visible frames to be everything. This will automatically zoom the window to the maximum
          // extent of the polygon
          if (payload.drawnPolygon)
          {
            thunkAPI.dispatch(setActiveYear(0))
            thunkAPI.dispatch(setVisibleFrames(payload.frameDetails))
          } else if (!activeYear || ! yearsArrayFromFrames.indexOf(activeYear))
          {
            thunkAPI.dispatch(setActiveYear(yearsArrayFromFrames[0]))
            thunkAPI.dispatch(setVisibleFrames(APUtils.getFramesForYear(payload.frameDetails, yearsArrayFromFrames[0])))
          }


        }
      }
    } catch (e) {
      if (e instanceof RecaptchaRequiredError) {
        const action: RecaptchaAction<SelectedParcelProps> = {
          type: FETCH_AP_PROJECT_DETAILS,
          payload: selectedParcel,
        }
        thunkAPI.dispatch(forceManualRecaptcha(action))
      } else {
        throw e
      }
    }
  })

export interface ReverseGeocodingResponse {
  Latitude: string | number,
  Longitude: string | number,
  LAISKEY: string,
  DATUM: string,
  City: string,
  Country: string,
  HouseNumber: string | number,
  Locality: string,
  PostalCode: string | number,
  StateProvince: string,
  StreetName: string,
  StreetType: string,
  Level: string | number,
  FormattedAddress: string
}

export const UPDATE_ADVANCED_SEARCH_FILTERS = 'UPDATE_ADVANCED_SEARCH_FILTERS'
export const RESET_ADVANCED_SEARCH_FILTERS = 'RESET_ADVANCED_SEARCH_FILTERS'

export type UpdateSearchFiltersAction = {
  type: string
  payload: SearchFilters
}

export const updateAdvancedSearchFilters = (searchFilters: SearchFilters): UpdateSearchFiltersAction => {
  return {
    type: UPDATE_ADVANCED_SEARCH_FILTERS,
    payload: searchFilters,
  }
}

export const advancedSearchFiltersReducer = (searchFilters: SearchFilters = {}, {
  type,
  payload,
}: UpdateSearchFiltersAction): SearchFilters => {
  if (type === UPDATE_ADVANCED_SEARCH_FILTERS) {
    return payload
  }

  if (type === RESET_ADVANCED_SEARCH_FILTERS) {
    return {}
  }

  return searchFilters
}